import React, { useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  IconButton,
  Tooltip,
  Divider,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TextFields as TextFieldsIcon,
  Numbers as NumbersIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  CheckBox as CheckBoxIcon,
  RadioButtonChecked as RadioButtonIcon,
  ArrowDropDownCircle as DropdownIcon,
  Draw as SignatureIcon,
  BorderColor as InitialIcon,
  Person as PersonIcon,
  Work as TitleIcon,
  Business as CompanyIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { DocuSignTemplate } from '../types';

const DRAWER_WIDTH = 280;

// All 12 predefined DocuSign field types
interface FieldType {
  key: string;
  displayName: string;
  description: string;
  icon: React.ReactElement;
  defaultWidth: number;
  defaultHeight: number;
  defaultFontSize: number;
}

const FIELD_TYPES: FieldType[] = [
  { key: 'textTabs', displayName: 'Text Field', description: 'Single-line text input', icon: <TextFieldsIcon />, defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'numberTabs', displayName: 'Number Field', description: 'Numeric input', icon: <NumbersIcon />, defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'emailTabs', displayName: 'Email Field', description: 'Email address input', icon: <EmailIcon />, defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'dateTabs', displayName: 'Date Field', description: 'Date picker', icon: <CalendarIcon />, defaultWidth: 100, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'checkboxTabs', displayName: 'Checkbox', description: 'Checkbox input', icon: <CheckBoxIcon />, defaultWidth: 20, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'radioGroupTabs', displayName: 'Radio Button Group', description: 'Radio button group', icon: <RadioButtonIcon />, defaultWidth: 20, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'listTabs', displayName: 'Dropdown List', description: 'Dropdown selection', icon: <DropdownIcon />, defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'signHereTabs', displayName: 'Signature Field', description: 'Signature placement', icon: <SignatureIcon />, defaultWidth: 150, defaultHeight: 40, defaultFontSize: 12 },
  { key: 'initialHereTabs', displayName: 'Initial Field', description: 'Initial placement', icon: <InitialIcon />, defaultWidth: 80, defaultHeight: 30, defaultFontSize: 12 },
  { key: 'fullNameTabs', displayName: 'Full Name Field', description: 'Full name input', icon: <PersonIcon />, defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'titleTabs', displayName: 'Title Field', description: 'Job title input', icon: <TitleIcon />, defaultWidth: 150, defaultHeight: 20, defaultFontSize: 12 },
  { key: 'companyTabs', displayName: 'Company Field', description: 'Company name input', icon: <CompanyIcon />, defaultWidth: 200, defaultHeight: 20, defaultFontSize: 12 },
];

interface FieldPaletteProps {
  template: DocuSignTemplate | null;
  selectedFieldType: string | null;
  onFieldTypeSelect: (fieldType: string, defaults: { width: number; height: number; fontSize: number }) => void;
  onFieldDragStart: (fieldType: string, defaults: { width: number; height: number; fontSize: number }) => void;
  onDeleteAllFields: () => void;
  isOpen: boolean;
  onToggle: () => void;
}

const FieldPalette: React.FC<FieldPaletteProps> = ({
  template,
  selectedFieldType,
  onFieldTypeSelect,
  onFieldDragStart,
  onDeleteAllFields,
  isOpen,
  onToggle,
}) => {

  // Calculate usage count for each field type
  const fieldUsageCounts = useMemo(() => {
    if (!template) return {};

    const counts: Record<string, number> = {};
    
    // Initialize all field types with 0
    FIELD_TYPES.forEach(fieldType => {
      counts[fieldType.key] = 0;
    });

    // Count fields in template
    template.recipients.signers.forEach(signer => {
      Object.entries(signer.tabs).forEach(([tabType, tabs]) => {
        if (tabs && Array.isArray(tabs)) {
          counts[tabType] = (counts[tabType] || 0) + tabs.length;
        }
      });
    });

    return counts;
  }, [template]);

  const handleToggle = () => {
    onToggle();
  };

  const handleFieldClick = (fieldType: FieldType) => {
    onFieldTypeSelect(fieldType.key, {
      width: fieldType.defaultWidth,
      height: fieldType.defaultHeight,
      fontSize: fieldType.defaultFontSize,
    });
  };

  const handleDragStart = (e: React.DragEvent, fieldType: FieldType) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('fieldType', fieldType.key);
    e.dataTransfer.setData('defaultWidth', fieldType.defaultWidth.toString());
    e.dataTransfer.setData('defaultHeight', fieldType.defaultHeight.toString());
    e.dataTransfer.setData('defaultFontSize', fieldType.defaultFontSize.toString());
    
    onFieldDragStart(fieldType.key, {
      width: fieldType.defaultWidth,
      height: fieldType.defaultHeight,
      fontSize: fieldType.defaultFontSize,
    });
  };

  return (
    <>
      {/* Toggle button when drawer is closed */}
      {!isOpen && (
        <IconButton
          onClick={handleToggle}
          sx={{
            position: 'fixed',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: (theme) => theme.zIndex.drawer - 1,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '0 8px 8px 0',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}

      {/* Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={isOpen}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            top: { xs: 56, sm: 64 },
            height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" component="div">
              Field Palette
            </Typography>
            <IconButton onClick={handleToggle} size="small">
              <ChevronLeftIcon />
            </IconButton>
          </Box>

          {/* Field Types List */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <List>
              {FIELD_TYPES.map((fieldType) => {
                const usageCount = fieldUsageCounts[fieldType.key] || 0;
                const isUsed = usageCount > 0;
                const isSelected = selectedFieldType === fieldType.key;

                return (
                  <Tooltip
                    key={fieldType.key}
                    title={fieldType.description}
                    placement="right"
                    arrow
                  >
                    <ListItem disablePadding>
                      <ListItemButton
                        selected={isSelected}
                        onClick={() => handleFieldClick(fieldType)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, fieldType)}
                        sx={{
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            '&:hover': {
                              backgroundColor: 'primary.light',
                            },
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Badge
                            badgeContent={usageCount}
                            color="primary"
                            invisible={usageCount === 0}
                          >
                            {fieldType.icon}
                          </Badge>
                        </ListItemIcon>
                        <ListItemText
                          primary={fieldType.displayName}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontWeight: isSelected ? 'bold' : 'normal' },
                          }}
                        />
                        <ListItemIcon sx={{ minWidth: 'auto' }}>
                          {isUsed ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            <CancelIcon color="error" fontSize="small" />
                          )}
                        </ListItemIcon>
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>
                );
              })}
            </List>
          </Box>

          <Divider />

          {/* Footer */}
          <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteSweepIcon />}
              fullWidth
              disabled={Object.values(fieldUsageCounts).every(c => c === 0)}
              onClick={() => {
                if (window.confirm('Delete all fields from the template? This cannot be undone.')) {
                  onDeleteAllFields();
                }
              }}
              sx={{ mb: 1.5, textTransform: 'none', borderRadius: 2 }}
            >
              Delete all fields
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              Click to select, then click on PDF to place
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Or drag and drop onto PDF
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default FieldPalette;
